import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { joinAnotherLeague } from "@/lib/access.functions";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/_app/join-league")({
  component: JoinLeaguePage,
  head: () => ({ meta: [{ title: "Join a league — Bracket Challenge" }] }),
});

function JoinLeaguePage() {
  const navigate = useNavigate();
  const joinFn = useAuthedServerFn(joinAnotherLeague);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await joinFn({ data: { code: code.trim() } });
      if (res.alreadyMember) {
        track("league_join_attempt", { result: "already_member" });
        toast.success(`You're already in ${res.leagueName}`);
        navigate({ to: "/home" });
      } else if (res.alreadyPending) {
        track("league_join_attempt", { result: "already_pending" });
        toast.info(`Request to join ${res.leagueName} is already pending`);
        navigate({ to: "/home" });
      } else {
        track("league_join_attempt", { result: "requested" });
        toast.success(`Request submitted! An admin will review it.`);
        navigate({ to: "/home" });
      }
    } catch (err: any) {
      toast.error(err?.message || "Invalid code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="max-w-md mx-auto px-4 py-8">
      <Link to="/home" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Home
      </Link>
      <h1 className="font-display text-2xl font-bold text-foreground mb-6">Join a league</h1>
      <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-border bg-card p-5">
        <div>
          <Label>League code</Label>
          <Input
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="FAMILY-X7K2A"
            className="font-mono uppercase"
            maxLength={32}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Ask the admin who runs the league for the code.
          </p>
        </div>
        <Button type="submit" disabled={submitting}>{submitting ? "Submitting…" : "Request to join"}</Button>
      </form>
    </main>
  );
}
